import { nowUtc } from '@/lib/clock';
import { newUuid } from '@/lib/ids';
import { db as defaultDb, type GentleTaskDatabase } from '@/data/db/database';
import type { Contact, TaskContact, TaskContactRole, Uuid } from '@/data/db';
import { NotFoundError, bumpAudit, markDataMutation, newAudit } from './support';

export interface CreateContactInput {
  name: string;
  jobTitle?: string | null;
  contactTypeId?: Uuid | null;
  relationshipId?: Uuid | null;
  organization?: string | null;
  department?: string | null;
  phone?: string | null;
  email?: string | null;
  note?: string | null;
  tagIds?: Uuid[];
}

export type UpdatableContactFields = Partial<
  Omit<Contact, keyof ReturnType<typeof newAudit> | 'id' | 'deletedAt'>
>;

export function createContactRepository(db: GentleTaskDatabase = defaultDb) {
  const writeTables = [db.contacts, db.taskContacts, db.followUpLogs, db.settings];

  async function create(input: CreateContactInput): Promise<Contact> {
    const timestamp = nowUtc();
    const contact: Contact = {
      id: newUuid(),
      name: input.name,
      jobTitle: input.jobTitle ?? null,
      contactTypeId: input.contactTypeId ?? null,
      relationshipId: input.relationshipId ?? null,
      organization: input.organization ?? null,
      department: input.department ?? null,
      phone: input.phone ?? null,
      email: input.email ?? null,
      note: input.note ?? null,
      tagIds: input.tagIds ?? [],
      deletedAt: null,
      ...newAudit(timestamp),
    };

    await db.transaction('rw', writeTables, async () => {
      await db.contacts.add(contact);
      await markDataMutation(db, timestamp);
    });

    return contact;
  }

  /** 修改聯絡人時，同步更新所有指向它的快照（data-model 3.4）。 */
  async function update(id: Uuid, patch: UpdatableContactFields): Promise<Contact> {
    const timestamp = nowUtc();
    let result!: Contact;

    await db.transaction('rw', writeTables, async () => {
      const current = await db.contacts.get(id);
      if (!current) throw new NotFoundError('聯絡人', id);

      const next: Contact = { ...current, ...patch, ...bumpAudit(current, timestamp) };
      await db.contacts.put(next);

      const links = await db.taskContacts.where('contactId').equals(id).toArray();
      for (const link of links) {
        await db.taskContacts.put({
          ...link,
          nameSnapshot: next.name,
          jobTitleSnapshot: next.jobTitle,
          organizationSnapshot: next.organization,
          ...bumpAudit(link, timestamp),
        });
      }

      await markDataMutation(db, timestamp);
      result = next;
    });

    return result;
  }

  async function softDelete(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.contacts.get(id);
      if (!current) throw new NotFoundError('聯絡人', id);
      // 快照與 contactId 都不動，還原後即可恢復連結。
      await db.contacts.put({ ...current, deletedAt: timestamp, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  async function restore(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const current = await db.contacts.get(id);
      if (!current) throw new NotFoundError('聯絡人', id);
      await db.contacts.put({ ...current, deletedAt: null, ...bumpAudit(current, timestamp) });
      await markDataMutation(db, timestamp);
    });
  }

  /**
   * 永久刪除。歷史任務不受影響：
   * 關聯的 contactId 設為 null、快照凍結為最後狀態；
   * 追蹤紀錄只把這個 id 從名單移除，內容完整保留。
   */
  async function purge(id: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const links = await db.taskContacts.where('contactId').equals(id).toArray();
      for (const link of links) {
        await db.taskContacts.put({ ...link, contactId: null, ...bumpAudit(link, timestamp) });
      }

      const logs = await db.followUpLogs.where('contactIds').equals(id).toArray();
      for (const log of logs) {
        await db.followUpLogs.put({
          ...log,
          contactIds: log.contactIds.filter((candidate) => candidate !== id),
          ...bumpAudit(log, timestamp),
        });
      }

      await db.contacts.delete(id);
      await markDataMutation(db, timestamp);
    });
  }

  async function linkToTask(
    taskId: Uuid,
    contactId: Uuid,
    roleInTask: TaskContactRole | null,
  ): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const contact = await db.contacts.get(contactId);
      if (!contact) throw new NotFoundError('聯絡人', contactId);

      const existing = await db.taskContacts
        .where('[taskId+contactId]')
        .equals([taskId, contactId])
        .first();

      const snapshot = {
        nameSnapshot: contact.name,
        jobTitleSnapshot: contact.jobTitle,
        organizationSnapshot: contact.organization,
      };

      if (existing) {
        await db.taskContacts.put({
          ...existing,
          roleInTask,
          ...snapshot,
          ...bumpAudit(existing, timestamp),
        });
      } else {
        const link: TaskContact = {
          id: newUuid(),
          taskId,
          contactId,
          roleInTask,
          ...snapshot,
          ...newAudit(timestamp),
        };
        await db.taskContacts.add(link);
      }

      await markDataMutation(db, timestamp);
    });
  }

  async function unlinkFromTask(taskId: Uuid, contactId: Uuid): Promise<void> {
    const timestamp = nowUtc();
    await db.transaction('rw', writeTables, async () => {
      const existing = await db.taskContacts
        .where('[taskId+contactId]')
        .equals([taskId, contactId])
        .first();
      if (existing) await db.taskContacts.delete(existing.id);
      await markDataMutation(db, timestamp);
    });
  }

  async function listTaskContacts(taskId: Uuid): Promise<TaskContact[]> {
    return db.taskContacts.where('taskId').equals(taskId).toArray();
  }

  async function listActive(): Promise<Contact[]> {
    const all = await db.contacts.orderBy('name').toArray();
    return all.filter((contact) => contact.deletedAt === null);
  }

  async function listDeleted(): Promise<Contact[]> {
    const all = await db.contacts.orderBy('name').toArray();
    return all.filter((contact) => contact.deletedAt !== null);
  }

  return {
    create,
    update,
    softDelete,
    restore,
    purge,
    linkToTask,
    unlinkFromTask,
    listTaskContacts,
    listActive,
    listDeleted,
    get: (id: Uuid) => db.contacts.get(id),
  };
}

export type ContactRepository = ReturnType<typeof createContactRepository>;
export const contactRepository = createContactRepository();
